import { initMarkdownEditor, setupToolbar, insertContextRef, insertVariable } from "./index";
import type { EditorView } from "@codemirror/view";
import type { WorkflowVariableDef } from "./variables";

export const MarkdownEditorComponent = (
  placeholder: string,
  minHeight: string,
  flexGrow: boolean,
  readOnly: boolean = false,
  enableWikilinks: boolean = false,
  enableContextReferences: boolean = false,
  workflowId: string = "",
  enableVariables: boolean = false,
  workflowVariablesJSON: WorkflowVariableDef[] = [],
) => ({
  _editorView: null as EditorView | null,
  // _suppressNextChange is a one-shot flag consumed by the NEXT debounced
  // onChange flush. Set by setContent(md, {suppress:true}) — the CMS
  // editor's suppressed machine-fill path (cms_field_filled on-screen
  // apply / _setFieldValue) — so a programmatic content replacement
  // updates the hidden input (autosave reads it on the next real edit)
  // WITHOUT dispatching 'markdown-change' (which would re-arm autosave and
  // trip the operator-edit AI-layer clear for a fill that isn't a human
  // edit). Unsuppressed callers (the revert button popping a layer) leave
  // this false, so the natural dispatch → autosave path runs.
  _suppressNextChange: false,

  init() {
    const container = (this as any).$refs.editorContainer as HTMLElement;
    const hiddenInput = (this as any).$refs.hiddenInput as HTMLInputElement;
    const toolbar = (this as any).$refs.toolbar as HTMLElement;

    // Read initial content from the hidden input's value attribute.
    const initialContent = hiddenInput.value || "";

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const workflowVariables = enableVariables ? workflowVariablesJSON : undefined;

    this._editorView = initMarkdownEditor(container, {
      content: initialContent,
      placeholder,
      minHeight,
      flexGrow,
      readOnly,
      enableWikilinks,
      enableContextReferences,
      workflowId,
      enableVariables,
      workflowVariables,
      toolbar: toolbar || null,
      onChange: (content: string) => {
        if (debounceTimer) clearTimeout(debounceTimer);
        // KNOWN BOUNDED EDGE: _suppressNextChange is consumed here, up to
        // 100ms after setContent() set it, not by the setContent-triggered
        // change itself. A real keystroke landing inside that window
        // resets debounceTimer (single shared timer), so the flush that
        // finally fires — carrying the keystroke's up-to-date content —
        // is the one that consumes the flag, swallowing that one
        // 'markdown-change' dispatch. Impact is bounded: hiddenInput.value
        // already reflects the latest content, so nothing is lost — the
        // dispatch (and therefore autosave/AI-layer-clear) is merely
        // deferred to the next real edit. Not worth a same-content guard
        // for this rare, self-healing race.
        debounceTimer = setTimeout(() => {
          hiddenInput.value = content;
          if (this._suppressNextChange) {
            this._suppressNextChange = false;
          } else {
            hiddenInput.dispatchEvent(new CustomEvent("markdown-change", { bubbles: true }));
          }
        }, 100);
      },
    });

    // Connect toolbar to editor
    if (toolbar && this._editorView) {
      setupToolbar(toolbar, this._editorView);

      // Add @ button for context references if enabled
      if (enableContextReferences) {
        const ctxRefBtn = toolbar.querySelector(
          'button[data-command="context-ref"]',
        ) as HTMLElement | null;
        if (ctxRefBtn) {
          ctxRefBtn.addEventListener("click", (e) => {
            e.preventDefault();
            if (this._editorView) {
              insertContextRef(this._editorView);
              this._editorView.focus();
            }
          });
        }
      }

      // Add {{ }} button for variable insertion if enabled
      if (enableVariables) {
        const varBtn = toolbar.querySelector(
          'button[data-command="insert-variable"]',
        ) as HTMLElement | null;
        if (varBtn) {
          varBtn.addEventListener("click", (e) => {
            e.preventDefault();
            if (this._editorView) {
              insertVariable(this._editorView);
              this._editorView.focus();
            }
          });
        }
      }
    }

    // Listen for insert-references custom event (from command palette)
    if (enableContextReferences && this._editorView) {
      const editorView = this._editorView;
      container.addEventListener("insert-references", ((e: CustomEvent) => {
        const refs: Array<{ label: string; type: string; id: string }> = e.detail?.references || [];
        if (refs.length === 0 || !editorView) return;

        const { from, to } = editorView.state.selection.main;
        const insertText = refs.map((r) => `@[${r.label}](ref:${r.type}:${r.id})`).join(" ");
        editorView.dispatch({
          changes: { from, to, insert: insertText + " " },
          selection: { anchor: from + insertText.length + 1 },
        });
        editorView.focus();
      }) as EventListener);
    }

    // Listen for md-set-content custom event (full-document programmatic
    // fill, e.g. a gallery description template applied from the CMS
    // sidebar). Suppressed like any other setContent() call so it doesn't
    // re-arm autosave / trip the AI-layer clear — see setContent's comment.
    container.addEventListener("md-set-content", ((e: CustomEvent) => {
      const md = e.detail?.markdown ?? "";
      (this as any).setContent(md, { suppress: true });
    }) as EventListener);

    // Click anywhere in the editor area focuses CM6.
    // CM6's .cm-content may not fill the full container height,
    // so clicks in the empty space below content would miss it.
    const view = this._editorView;
    container.addEventListener("mousedown", (e) => {
      if (!view) return;
      const target = e.target as HTMLElement;
      // Only intercept clicks that land outside .cm-content
      // (clicks on .cm-content are handled by CM6 natively)
      if (!target.closest(".cm-content")) {
        e.preventDefault();
        view.focus();
        const end = view.state.doc.length;
        view.dispatch({ selection: { anchor: end } });
      }
    });

    // Expose view for external consumers (e.g., insertVariable)
    (this as any).editorView = this._editorView;
  },

  destroy() {
    this._editorView?.destroy();
    this._editorView = null;
  },

  getView(): EditorView | null {
    return this._editorView;
  },

  insertAtCursor(text: string) {
    const view = this._editorView;
    if (!view) return;
    const { from, to } = view.state.selection.main;
    view.dispatch({
      changes: { from, to, insert: text },
      selection: { anchor: from + text.length },
    });
    view.focus();
  },

  // setContent replaces the ENTIRE document — the external-drive entry
  // point the CMS editor uses for both directions of the revert stack
  // (design spec §7.2) and the on-screen `cms_field_filled` live-apply
  // path (static/js/cms/editor.ts _setFieldValue): a plain
  // `hiddenInput.value = x` would desync the visible CodeMirror view from
  // the value autosave reads, since CM6 is the source of truth here (the
  // hidden input only mirrors it one-way, via onChange). Dispatching a
  // full-doc replacement re-enters the SAME onChange path a real edit
  // takes, so `{suppress:true}` (opts) is threaded through to skip just
  // the one resulting 'markdown-change' dispatch — see
  // _suppressNextChange. Falls back to setting the hidden input directly
  // if the view hasn't mounted yet (shouldn't happen in practice).
  setContent(md: string, opts: { suppress?: boolean } = {}) {
    if (opts.suppress) this._suppressNextChange = true;
    const view = this._editorView;
    if (!view) {
      const hiddenInput = (this as any).$refs?.hiddenInput as HTMLInputElement | undefined;
      if (hiddenInput) hiddenInput.value = md;
      this._suppressNextChange = false;
      return;
    }
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: md },
    });
  },
});
